import React from "react";
import Card from "../../../apps/admin/theme/untitled/Card/Card";
import "./PlanUsageChartSkeleton.scss";

/**
 * @file PlanUsageChartSkeleton.js
 * @description Shimmer loading placeholder for {@link PlanUsageChart}.
 *
 * Mirrors the exact visual structure of the real component — card, header,
 * bar chart area, and sidebar — so the layout does not shift when data arrives.
 * Import and render while `loading` is true in the parent.
 *
 * @example
 * // Typical usage inside PlanUsageChart
 * if (loading) return <PlanUsageChartSkeleton title={title} />;
 */

// ─── Skeleton primitives ──────────────────────────────────────────────────────

/**
 * Horizontal rule between sidebar sections.
 *
 * @returns {JSX.Element}
 */
const SkDivider = () => <div className="r-usage-sk-divider" />;

// ─── Sidebar skeleton ─────────────────────────────────────────────────────────

/**
 * Skeleton for the right-hand sidebar — month number, progress bar,
 * dot rows, billing date, and recommendation panel.
 *
 * @returns {JSX.Element}
 */
const SkSidebar = () => (
  <div className="r-usage-sk-sidebar">
    {/* Month label + big number */}
    <div className="r-usage-sk-month-block">
      <div className="r-usage-sk-month-label" />
      <div className="r-usage-sk-row">
        <div className="r-usage-sk-big-number" />
        <div className="r-usage-sk-quota-number" />
      </div>
      <div className="r-usage-sk-progress" />
      <div className="r-usage-sk-row">
        <div className="r-usage-sk-pct" />
        <div className="r-usage-sk-status" />
      </div>
    </div>

    <SkDivider />

    {/* Dot rows — current / projected / included */}
    <div className="r-usage-sk-dotrows">
      {[100, 80, 90].map((labelWidth, i) => (
        <div key={i} className="r-usage-sk-row">
          <div className="r-usage-sk-row-start">
            <div className="r-usage-sk-dot" />
            <div className="r-usage-sk-dot-label" style={{ width: labelWidth }} />
          </div>
          <div className="r-usage-sk-dot-value" />
        </div>
      ))}
    </div>

    {/* Billing date row */}
    <SkDivider />
    <div className="r-usage-sk-row">
      <div className="r-usage-sk-dot-label" style={{ width: 56 }} />
      <div className="r-usage-sk-dot-value" style={{ width: 80 }} />
    </div>
  </div>
);

// ─── Bar chart skeleton ───────────────────────────────────────────────────────

/**
 * Twelve placeholder bars that loosely suggest a bar chart.
 * Heights are staggered in CSS (nth-child rules) to add visual rhythm
 * without implying any specific data shape.
 *
 * @returns {JSX.Element}
 */
const SkChart = () => (
  <div className="r-usage-sk-chart">
    {Array.from({ length: 12 }, (_, i) => (
      <div key={i} className="r-usage-sk-bar" />
    ))}
  </div>
);

// ─── PlanUsageChartSkeleton ───────────────────────────────────────────────────

/**
 * Full-card shimmer placeholder for {@link PlanUsageChart}.
 *
 * Renders the card shell, header, bar chart area, and sidebar as shimmer
 * blocks. The layout is pixel-matched to the real component so there is
 * no reflow when data replaces the skeleton.
 *
 * @param {object}  props
 * @param {string}  [props.title='Plan usage']  Card heading — shown as a real
 *   string so the page title does not flicker on load.
 * @returns {JSX.Element}
 *
 * @example
 * const { data, loading } = useFetch(url);
 * if (loading) return <PlanUsageChartSkeleton title="Plan usage" />;
 * return <PlanUsageChart ... />;
 */
const PlanUsageChartSkeleton = ({ title = "Plan usage" }) => (
  <Card>
    <Card.Header title={title}>
      <div className="r-usage-sk-legend" />
    </Card.Header>
    <Card.Body>
      {/* Body */}
      <div className="r-usage-sk-body">
        <SkChart />
        <SkSidebar />
      </div>

      {/* Recommendation panel */}
      <div className="r-usage-sk-reco-row">
        <div className="r-usage-sk-reco-col">
          <div className="r-usage-sk-reco">
            <div className="r-usage-sk-reco-titlerow">
              <div className="r-usage-sk-reco-icon" />
              <div className="r-usage-sk-reco-title" />
            </div>
            <div className="r-usage-sk-reco-lines">
              <div className="r-usage-sk-reco-line" />
              <div className="r-usage-sk-reco-line" />
            </div>
            <div className="r-usage-sk-reco-plan">
              <div>
                <div className="r-usage-sk-reco-plan-name" />
                <div className="r-usage-sk-reco-plan-quota" />
              </div>
              <div className="r-usage-sk-reco-plan-price" />
            </div>
            <div className="r-usage-sk-reco-cta" />
          </div>
        </div>
      </div>
    </Card.Body>
  </Card>
);

export default PlanUsageChartSkeleton;
